import { useState } from 'react'
import type { JSX } from 'react'

import type { Channel as UserChannel } from '../../../../../common/types'

import {
  Avatar,
  ChannelAvatar,
  CheckboxCard,
  Flex,
  InfoIcon,
  Notice,
  Text,
} from '@buffer-mono/popcorn'
import type { ChannelType } from '@buffer-mono/popcorn'

import styles from '../index.module.css'
import { CancelFlowStepFrame } from './CancelFlowStepFrame'

export const FREE_PLAN_CHANNEL_LIMIT = 3

const SUPPORTED_CHANNEL_TYPES = [
  'facebook',
  'googlebusiness',
  'instagram',
  'instagram_login_api',
  'linkedin',
  'mastodon',
  'pinterest',
  'startPage',
  'tiktok',
  'twitter',
  'youtube',
  'x',
  'threads',
  'bluesky',
  'substack',
  'whatsapp',
] satisfies ChannelType[]

const CHANNEL_TYPES = new Set<string>(SUPPORTED_CHANNEL_TYPES)

const CHANNEL_DISPLAY_NAME: Record<ChannelType, string> = {
  facebook: 'Facebook',
  googlebusiness: 'Google',
  instagram: 'Instagram',
  instagram_login_api: 'Instagram',
  linkedin: 'LinkedIn',
  mastodon: 'Mastodon',
  pinterest: 'Pinterest',
  startPage: 'Start Page',
  tiktok: 'TikTok',
  twitter: 'X',
  youtube: 'YouTube',
  x: 'X',
  threads: 'Threads',
  bluesky: 'Bluesky',
  substack: 'Substack',
  whatsapp: 'WhatsApp',
}

export type ChannelRow = {
  id: string
  name: string
  network: string
  type: ChannelType | null
  avatar?: string
}

function isChannelType(value: string): value is ChannelType {
  return CHANNEL_TYPES.has(value)
}

function getChannelType(channel: UserChannel): ChannelType | null {
  if (isChannelType(channel.service)) {
    return channel.service
  }

  if (channel.type && isChannelType(channel.type)) {
    return channel.type
  }

  return null
}

function getChannelDisplayName(channel: UserChannel): string {
  if (isChannelType(channel.service)) {
    return CHANNEL_DISPLAY_NAME[channel.service]
  }

  return channel.service
}

function toChannelRow(channel: UserChannel): ChannelRow {
  return {
    id: channel.id,
    name: channel.name,
    network: getChannelDisplayName(channel),
    type: getChannelType(channel),
    avatar: channel.avatar,
  }
}

function getCreatedAtTime(channel: UserChannel): number {
  if (!channel.createdAt) return Number.MAX_SAFE_INTEGER

  const time = new Date(channel.createdAt).getTime()
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time
}

export function getSelectableChannels(
  channels: UserChannel[] = [],
): ChannelRow[] {
  const selectableChannels = channels.filter((channel) => !channel.isLocked)
  selectableChannels.sort((a, b) => getCreatedAtTime(a) - getCreatedAtTime(b))

  return selectableChannels.map(toChannelRow)
}

export function getDefaultSelectedChannelIds(
  channels: ChannelRow[],
): Set<string> {
  return new Set(
    channels.slice(0, FREE_PLAN_CHANNEL_LIMIT).map((channel) => channel.id),
  )
}

type ChannelsStepProps = {
  channels: ChannelRow[]
  initialSelectedChannelIds: string[]
  stepLabel?: string
  isTrialCancellation?: boolean
  onBack?: () => void
  onDismiss: () => void
  onContinue: (channelsToKeep: string[]) => void
}

export const ChannelsStep = ({
  channels,
  initialSelectedChannelIds,
  stepLabel,
  isTrialCancellation = false,
  onBack,
  onDismiss,
  onContinue,
}: ChannelsStepProps): JSX.Element => {
  const [selectedChannelIds, setSelectedChannelIds] = useState<Set<string>>(
    () =>
      initialSelectedChannelIds.length > 0
        ? new Set(initialSelectedChannelIds)
        : getDefaultSelectedChannelIds(channels),
  )
  const currentChannelIds = new Set(channels.map((channel) => channel.id))
  const selectedCurrentChannelIds = Array.from(selectedChannelIds).filter(
    (channelId) => currentChannelIds.has(channelId),
  )
  const hasReachedLimit =
    selectedCurrentChannelIds.length >= FREE_PLAN_CHANNEL_LIMIT

  const handleChannelToggle = (channelId: string): void => {
    setSelectedChannelIds((prev) => {
      const next = new Set(prev)
      if (next.has(channelId)) {
        next.delete(channelId)
        return next
      }

      const selectedCurrentCount = Array.from(prev).filter((id) =>
        currentChannelIds.has(id),
      ).length
      if (selectedCurrentCount >= FREE_PLAN_CHANNEL_LIMIT) {
        return prev
      }

      next.add(channelId)
      return next
    })
  }

  const renderChannelCard = (channel: ChannelRow): JSX.Element => {
    const isSelected = selectedChannelIds.has(channel.id)
    const isDisabled = hasReachedLimit && !isSelected

    return (
      <CheckboxCard
        key={channel.id}
        checked={isSelected}
        disabled={isDisabled}
        onCheckedChange={() => handleChannelToggle(channel.id)}
      >
        <Flex gap="sm" align="center">
          {/* The checkbox label already includes the channel name below.
              Keep avatar alt text empty so screen readers do not announce it twice. */}
          {channel.type ? (
            <ChannelAvatar
              size="small"
              alt=""
              src={channel.avatar}
              name={channel.name}
              channel={channel.type}
            />
          ) : (
            <Avatar
              size="small"
              alt=""
              src={channel.avatar}
              name={channel.name}
            />
          )}
          <Flex direction="column" gap="3xs" fullWidth>
            <Text size="sm" weight="medium">
              {channel.network}
            </Text>
            <Text size="sm" color="subtle">
              {channel.name}
            </Text>
          </Flex>
        </Flex>
      </CheckboxCard>
    )
  }

  return (
    <CancelFlowStepFrame
      onBack={onBack}
      stepLabel={stepLabel}
      secondaryAction={{
        children: isTrialCancellation ? 'Keep My Trial' : 'Keep My Plan',
        onClick: onDismiss,
        variant: 'tertiary',
      }}
      primaryAction={{
        children: isTrialCancellation
          ? 'Continue Cancelling Trial'
          : 'Continue Cancellation',
        disabled: selectedCurrentChannelIds.length === 0,
        onClick: () => onContinue(selectedCurrentChannelIds),
        variant: 'critical',
      }}
      title="Choose the Channels You Want to Keep"
      description={
        isTrialCancellation ? (
          <>
            When your trial ends, your account will move to the Free plan with
            up to {FREE_PLAN_CHANNEL_LIMIT} active channels. Choose which
            channels to keep active.
          </>
        ) : (
          <>
            Your Free plan includes up to {FREE_PLAN_CHANNEL_LIMIT} channels.
            The remaining channels will be deactivated when your plan changes.
          </>
        )
      }
      descriptionProps={{
        color: 'subtle',
        className: styles.channelsStepDescription,
      }}
      contentGap="lg"
    >
      {hasReachedLimit && (
        <div className={styles.channelLimitNotice}>
          <Notice
            variant="info"
            icon={<InfoIcon />}
            className={styles.channelLimitNoticeCard}
          >
            You’ve selected {selectedCurrentChannelIds.length} of{' '}
            {FREE_PLAN_CHANNEL_LIMIT} channels for your Free plan. Deselect one
            to choose another.
          </Notice>
        </div>
      )}
      <CheckboxCard.Group
        aria-label="Channels to keep on the Free plan"
        className={styles.channelCheckboxGroup}
      >
        {channels.map(renderChannelCard)}
      </CheckboxCard.Group>
    </CancelFlowStepFrame>
  )
}
